// Types for the auth feature.
//
// The payload `POST /auth/provision` returns is the `/settings` body — one wire
// type, `Settings` in `@/shared/types`, not a second copy of it here.

export type AuthMode = "login" | "signup";

/** Codes carried on `/login?error=<code>` when the callback cannot finish. */
export type AuthErrorCode =
  | "missing_token"
  | "unsupported_token"
  | "authenticate_failed"
  | "provision_failed"
  | "send_failed";

/** One sentence per code — the login page renders the reason inline. */
export const AUTH_ERROR_COPY: Record<AuthErrorCode, string> = {
  missing_token: "That sign-in link is missing its token. Start again below.",
  unsupported_token: "That sign-in link is not one we recognise. Start again below.",
  authenticate_failed: "That sign-in link has expired or was already used.",
  provision_failed: "You are signed in, but we could not set up your workspace. Try again.",
  send_failed: "We could not send that email. Check the address and try again.",
};

export const authErrorMessage = (code: string | null): string | null =>
  code ? AUTH_ERROR_COPY[code as AuthErrorCode] ?? "Something went wrong signing you in." : null;

/** What the auth pages show: our sentence, plus whatever the SDK actually said. */
export interface AuthError {
  text: string;
  reason?: string | null;
}

/**
 * The SDK's own words for a failure. Stytch rejects with
 * `{error_type, error_message}`; anything else is a plain Error. Our copy says
 * what to do next, this says what went wrong — dropping it is how "try again"
 * becomes a support ticket instead of a fix (00-PLAN.md, the no-silent rule).
 */
export function failureReason(err: unknown): string | null {
  const stytch = (err as { error_message?: unknown } | null)?.error_message;
  if (typeof stytch === "string" && stytch) return stytch;
  return err instanceof Error && err.message ? err.message : null;
}
